import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getApi } from '../../providers/AuthContext';
import PageWrapper from '../../components/PageWrapper';
import { defaultImage } from '../../settings/config';
import { Pencil, ArrowLeft, ExternalLink, AlertCircle, Calendar, CreditCard } from 'lucide-react';

const SkeletonProfile = () => (
  <div className="max-w-3xl mx-auto">
    <div className="rounded-2xl overflow-hidden bg-card border border-border/40">
      <div className="h-1.5 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted" />
      <div className="p-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Avatar skeleton */}
          <div className="flex flex-col items-center md:items-start shrink-0">
            <div className="skeleton w-36 h-36 rounded-full" />
          </div>
          {/* Details skeleton */}
          <div className="flex-1 space-y-4">
            <div className="skeleton h-8 w-48 rounded-lg" />
            <div className="skeleton h-4 w-full rounded-lg" />
            <div className="skeleton h-4 w-3/4 rounded-lg" />
            <div className="flex gap-3 mt-6">
              <div className="skeleton h-10 w-28 rounded-xl" />
              <div className="skeleton h-10 w-20 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = getApi();
  const [child, setChild] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChild = async () => {
      try {
        setError(null);
        const result = await api.get(`/api/v1/children/${id}`);
        setChild(result.child);
      } catch (err) {
        setError('Failed to load child.');
      } finally {
        setLoading(false);
      }
    };
    loadChild();
  }, [id]);

  return (
    <PageWrapper pageTitle={child ? child.first_name : 'Child'}>
      <div className="p-4 lg:p-6">
        {loading ? (
          <SkeletonProfile />
        ) : error ? (
          <div className="max-w-3xl mx-auto">
            <div className="p-4 bg-destructive/10 dark:bg-destructive/20 text-destructive border border-destructive/20 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="flex-1 text-sm font-medium">{error}</span>
              <Link to="/children" className="text-sm underline text-destructive hover:text-destructive/80 transition-colors">Go Back</Link>
            </div>
          </div>
        ) : child ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto"
          >
            <div className="rounded-2xl overflow-hidden bg-card border border-border/40 shadow-soft-md">
              {/* Accent bar */}
              <div className="h-1.5 bg-gradient-to-r from-primary via-accent to-secondary" />

              <div className="p-6 sm:p-8">
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Avatar section */}
                  <div className="flex flex-col items-center md:items-start shrink-0">
                    <div className="relative">
                      <div className="absolute -inset-1.5 rounded-full bg-gradient-to-tr from-primary via-accent to-secondary opacity-30 blur-sm" />
                      <div className="relative w-36 h-36 rounded-full overflow-hidden border-4 border-card shadow-soft-lg">
                        <img
                          alt={`${child.first_name} profile`}
                          src={child.image_url || defaultImage}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Preview link */}
                    <Link
                      to={`/preview/${child.id}`}
                      target="_blank"
                      className="mt-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent transition-colors no-underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Public profile
                    </Link>
                  </div>

                  {/* Details section */}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-2xl sm:text-3xl text-card-foreground mb-1">
                      {child.first_name}
                    </h2>

                    {/* Meta info chips */}
                    <div className="flex flex-wrap items-center gap-2 mt-3 mb-5">
                      {child.cards_count !== undefined && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-accent/10 dark:bg-accent/15 text-accent text-xs font-medium">
                          <CreditCard className="w-3 h-3" />
                          {child.cards_count} {child.cards_count === 1 ? 'card' : 'cards'}
                        </span>
                      )}
                      {child.created_at && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-muted text-muted-foreground text-xs font-medium">
                          <Calendar className="w-3 h-3" />
                          Added {new Date(child.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>

                    {/* Bio */}
                    {child.bio ? (
                      <div className="bg-muted/40 dark:bg-muted/20 rounded-xl p-4 border border-border/30">
                        <p className="text-foreground/80 leading-relaxed text-sm">{child.bio}</p>
                      </div>
                    ) : (
                      <div className="bg-muted/30 dark:bg-muted/10 rounded-xl p-4 border border-dashed border-border/40">
                        <p className="text-muted-foreground/60 text-sm italic">
                          No bio added yet. Add one to give this profile some personality!
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 mt-6">
                      <Link
                        to={`/children/${id}/edit`}
                        className="btn-tertiary py-2.5 px-5 rounded-xl no-underline gap-2"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit Profile
                      </Link>
                      <Link
                        to="/children"
                        className="btn-light py-2.5 px-5 rounded-xl no-underline text-inherit gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-muted-foreground mb-4">Child not found.</p>
            <Link to="/children" className="btn-light py-2.5 px-5 rounded-xl no-underline text-inherit gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Children
            </Link>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default Page;
